class DocumentDownloadService {
  private baseUrl: string;

  constructor() {
    this.baseUrl = process.env.REACT_APP_API_URL || 'http://localhost:5000';
  }

  /**
   * Baixa um documento pelo ID do documento
   */
  async downloadDocument(documentId: string): Promise<Blob> {
    const token = this.getAuthToken();
    if (!token) {
      throw new Error('Token de autenticação não encontrado');
    }

    const response = await fetch(`${this.baseUrl}/documents/download/${documentId}`, {
      method: 'GET',
      headers: {
        'Authorization': `Bearer ${token}`,
        'tenant_id': sessionStorage.getItem('@KnowInDoc:customerToken') || '',
        'customer_id': sessionStorage.getItem('@KnowInDoc:customer_id') || '',
        'user_id': sessionStorage.getItem('@KnowInDoc:user_id') || ''
      }
    });

    if (!response.ok) {
      if (response.status === 404) {
        throw new Error('Documento não encontrado');
      } else if (response.status === 400) {
        throw new Error('Documento sem caminho de armazenamento');
      } else if (response.status === 401) {
        throw new Error('Não autorizado. Faça login novamente.');
      } else if (response.status === 403) {
        throw new Error('Acesso negado a este documento');
      } else {
        throw new Error(`Erro ao baixar documento (${response.status})`);
      }
    }

    return await response.blob();
  }

  /**
   * Gera e baixa a prévia PDF de um edoc (layout A4, sem assinaturas/evidências)
   * para posicionamento WYSIWYG de assinaturas.
   */
  async previewPdf(documentId: string): Promise<Blob> {
    const token = this.getAuthToken();
    if (!token)
    {
      throw new Error('Token de autenticação não encontrado');
    }

    const response = await fetch(`${this.baseUrl}/documents/${documentId}/preview-pdf`, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${token}`,
        'tenant_id': sessionStorage.getItem('@KnowInDoc:customerToken') || '',
        'customer_id': sessionStorage.getItem('@KnowInDoc:customer_id') || '',
        'user_id': sessionStorage.getItem('@KnowInDoc:user_id') || '',
      },
    });

    if (!response.ok)
    {
      if (response.status === 404)
      {
        throw new Error('Prévia PDF não disponível para este documento');
      }
      if (response.status === 401)
      {
        throw new Error('Não autorizado. Faça login novamente.');
      }
      if (response.status === 403)
      {
        throw new Error('Acesso negado a este documento');
      }
      throw new Error(`Erro ao gerar prévia PDF (${response.status})`);
    }

    return await response.blob();
  }

  /**
   * Baixa um documento e inicia o download no navegador
   */
  async downloadAndSaveDocument(documentId: string, fileName?: string): Promise<void> {
    try {
      const blob = await this.downloadDocument(documentId);
      
      // Usar uma abordagem mais moderna para evitar alertas do navegador
      if (this.isModernBrowser()) {
        this.downloadWithModernMethod(blob, fileName || 'documento');
      } else {
        this.downloadWithLegacyMethod(blob, fileName || 'documento');
      }
    } catch (error) {
      console.error('Erro ao baixar documento:', error);
      throw error;
    }
  }

  /**
   * Baixa um documento por caminho de armazenamento
   */
  async downloadDocumentByPath(storagePath: string): Promise<Blob> {
    const token = this.getAuthToken();
    if (!token) {
      throw new Error('Token de autenticação não encontrado');
    }

    const response = await fetch(`${this.baseUrl}/documents/download/path/${encodeURIComponent(storagePath)}`, {
      method: 'GET',
      headers: {
        'Authorization': `Bearer ${token}`,
        'tenant_id': sessionStorage.getItem('@KnowInDoc:customerToken') || '',
        'customer_id': sessionStorage.getItem('@KnowInDoc:customer_id') || '',
        'user_id': sessionStorage.getItem('@KnowInDoc:user_id') || ''
      }
    });

    if (!response.ok) {
      if (response.status === 404) {
        throw new Error('Documento não encontrado');
      } else if (response.status === 400) {
        throw new Error('Caminho de armazenamento inválido');
      } else if (response.status === 401) {
        throw new Error('Não autorizado. Faça login novamente.');
      } else if (response.status === 403) {
        throw new Error('Acesso negado a este documento');
      } else {
        throw new Error(`Erro ao baixar documento (${response.status})`);
      }
    }

    return await response.blob();
  }

  /**
   * Baixa um documento por caminho e inicia o download no navegador
   */
  async downloadAndSaveDocumentByPath(storagePath: string, fileName?: string): Promise<void> {
    try {
      const blob = await this.downloadDocumentByPath(storagePath);
      
      // Usar uma abordagem mais moderna para evitar alertas do navegador
      if (this.isModernBrowser()) {
        this.downloadWithModernMethod(blob, fileName || 'documento');
      } else {
        this.downloadWithLegacyMethod(blob, fileName || 'documento');
      }
    } catch (error) {
      console.error('Erro ao baixar documento por caminho:', error);
      throw error;
    }
  }

  /**
   * Método moderno de download (evita alertas)
   */
  private downloadWithModernMethod(blob: Blob, fileName: string): void {
    try {
      // Criar URL do blob
      const url = window.URL.createObjectURL(blob);
      
      // Criar link invisível
      const link = document.createElement('a');
      link.style.display = 'none';
      link.href = url;
      link.download = this.sanitizeFileName(fileName);
      
      // Adicionar ao DOM, clicar e remover
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      
      // Limpar URL do blob após um delay
      setTimeout(() => {
        window.URL.revokeObjectURL(url);
      }, 100);
      
    } catch (error) {
      console.warn('Método moderno falhou, usando fallback:', error);
      this.downloadWithLegacyMethod(blob, fileName);
    }
  }

  /**
   * Método legado de download (fallback)
   */
  private downloadWithLegacyMethod(blob: Blob, fileName: string): void {
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = this.sanitizeFileName(fileName);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
  }

  /**
   * Verifica se o navegador suporta métodos modernos
   */
  private isModernBrowser(): boolean {
    return !!(window.URL && window.URL.createObjectURL && window.Blob && typeof window.URL.createObjectURL === 'function');
  }

  /**
   * Sanitiza o nome do arquivo para evitar problemas
   */
  private sanitizeFileName(fileName: string): string {
    // Remove caracteres inválidos para nomes de arquivo
    return fileName
      .replace(/[<>:"/\\|?*]/g, '_')
      .replace(/\s+/g, '_')
      .substring(0, 255); // Limita o tamanho
  }

  /**
   * Salva um blob como arquivo no navegador (método antigo - mantido para compatibilidade)
   */
  private saveBlobAsFile(blob: Blob, fileName: string): void {
    this.downloadWithModernMethod(blob, fileName);
  }

  /**
   * Obtém o token de autenticação do sessionStorage
   */
  private getAuthToken(): string | null {
    return sessionStorage.getItem('@KnowInDoc:accessToken');
  }
}

export const documentDownloadService = new DocumentDownloadService();
export default documentDownloadService; 